import React, { useRef } from 'react';
import { Box, Typography, Grid, Button, Divider } from '@mui/material';
import html2canvas from 'html2canvas';
import JsPDF from 'jspdf';
// SVGs as ReactComponent
import { ReactComponent as BhimLogo } from '../../../public/assets/bhim.svg';
import { ReactComponent as UpiLogo } from '../../../public/assets/upi.svg';
import { ReactComponent as GooglePayLogo } from '../../../public/assets/googlepay.svg';
import { ReactComponent as PhonePeLogo } from '../../../public/assets/phonepe.svg';
import { ReactComponent as PaytmLogo } from '../../../public/assets/paytm.svg';
import { ReactComponent as BhimLogoIcon } from '../../../public/assets/IOB/bhimUpi.svg';
import { ReactComponent as DownloadIcon } from '../../../public/assets/downloadIcon.svg';
import { ReactComponent as ShareIcon } from '../../../public/assets/shareIcon.svg';

import QRCodeGenerator from './QRCodeGenerator';

export default function IobScanAndPayCard({ qrString, merchantVpa, merchantName }) {
  const cardRef = useRef(null);

  const generatePDF = async () => {
    if (!cardRef.current) return null;

    const canvas = await html2canvas(cardRef.current, { scale: 2 });
    const imgData = canvas.toDataURL('image/png');

    const pdf = new JsPDF({
      orientation: 'portrait',
      unit: 'px',
      format: [canvas.width, canvas.height]
    });

    pdf.addImage(imgData, 'PNG', 0, 0, canvas.width, canvas.height);
    return pdf;
  };

  const handleDownloadPDF = async () => {
    const pdf = await generatePDF();
    if (pdf) pdf.save(`${merchantVpa || 'MerchantCard'}.pdf`);
  };

  const handleSharePDF = async () => {
    const pdf = await generatePDF();
    if (!pdf) return;

    const pdfBlob = pdf.output('blob');
    const file = new File([pdfBlob], `${merchantVpa || 'MerchantCard'}.pdf`, {
      type: 'application/pdf'
    });

    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      try {
        await navigator.share({
          title: 'Merchant QR Card',
          text: 'Here is my QR card.',
          files: [file]
        });
      } catch (err) {
        console.error('Share failed:', err);
      }
    } else {
      // fallback for unsupported browsers
      pdf.save(`${merchantVpa || 'MerchantCard'}.pdf`);
      alert('Sharing is not supported on this device/browser. PDF downloaded instead.');
    }
  };

  return (
    <Box sx={{ textAlign: 'center', width: '80%', mx: 'auto' }}>
      {/* Outer container with gradient background */}
      <Box
        ref={cardRef}
        sx={{
          maxWidth: '100%',
          background: 'linear-gradient(180deg, #f79721 0%, #f79721 30%, #108341 70%, #108341 100%)',
          borderRadius: 3,
          py: 1.5,
          px: 1,
          boxShadow: 3
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            gap: 2,
            mb: 1,
            mt: 2
          }}
        >
          <img
            src="/assets/IOB/iob_logo.png"
            alt="IOB Logo"
            style={{ height: '90%', width: '100%', borderRadius: 20 }}
          />
        </Box>
        {/* Inner white card container */}
        <Box
          sx={{
            backgroundColor: 'white',
            borderRadius: 2,
            overflow: 'hidden',
            boxShadow: 2,
            mb: 2
          }}
        >
          {/* Header */}

          {/* Content inside white card */}
          <Box sx={{ p: 2 }}>
            {/* Scan & Pay */}
            <Typography
              variant="h6"
              sx={{
                mb: 1,
                color: 'primary.main',
                fontWeight: 600
              }}
            >
              Scan & Pay
            </Typography>

            <QRCodeGenerator qrString={qrString} />

            <Typography
              variant="body1"
              sx={{ color: 'primary.main', fontWeight: 600, mt: 1, fontFamily: 'Montserrat' }}
            >
              Merchant Name:{' '}
              <Box component="span" sx={{ fontWeight: 800 }}>
                {merchantName}
              </Box>
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: 'primary.main', fontWeight: 600, fontFamily: 'Montserrat' }}
            >
              UPI ID:{' '}
              <Box component="span" sx={{ fontWeight: 800 }}>
                {merchantVpa}
              </Box>
            </Typography>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                mt: 2,
                mb: 8
              }}
            >
              <BhimLogo style={{ height: 50 }} />
              <Divider
                orientation="vertical"
                sx={{
                  ml: 1,
                  width: '2px',
                  backgroundColor: 'grey',
                  height: '30px',
                  alignSelf: 'center'
                }}
              />
              <UpiLogo style={{ height: 50 }} />
            </Box>
            <Grid container spacing={1} justifyContent="center" mt={1}>
              {[
                { component: GooglePayLogo, name: 'Google Pay' },
                { component: PhonePeLogo, name: 'PhonePe' },
                { src: '/assets/IOB/bhim-iob.png', name: 'BHIM IOB', highlight: true },
                { component: BhimLogoIcon, name: 'BHIM UPI' },
                { component: PaytmLogo, name: 'Paytm' }
              ].map(({ component: LogoComponent, src, name, highlight }, idx) => (
                <Grid
                  item
                  key={idx}
                  xs={4}
                  sm={2}
                  md={2.3}
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  sx={{
                    mt: highlight ? -7 : 0
                  }}
                >
                  {src ? (
                    <img src={src} alt={name} style={{ height: 50, width: 50, borderRadius: 50 }} />
                  ) : (
                    <LogoComponent style={{ height: 40, width: 40 }} />
                  )}

                  <Typography variant="caption" textAlign="center" fontWeight="600">
                    {name}
                  </Typography>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Box>
      </Box>

      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', gap: 2 }}>
        <Button
          variant="contained"
          onClick={handleDownloadPDF}
          endIcon={<DownloadIcon />}
          sx={{ p: 1, width: '50%' }}
        >
          Download
        </Button>
        <Button
          variant="outlined"
          onClick={handleSharePDF}
          endIcon={<ShareIcon sx={{ color: 'primary.main' }} />}
          sx={{ p: 1, width: '50%' }}
        >
          Share
        </Button>
      </Box>
    </Box>
  );
}
